import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components/macro";
import { useLoadScript } from "@react-google-maps/api";
import { useMediaQuery } from "react-responsive";
import { Map, List, ChevronDown } from "react-feather";
import {
  Header,
  MapContainer,
  Legend,
  Card,
  DetailsPopup,
  Preloader,
} from "../components";

import CONSTANTS from "../constants";
import { Logo } from "../icons";
import { calcDistance } from "../helpers";
import { Modal, palette, mediaMin, fonts } from "../ui-components";
import SORTTYPES from "../enums/sort-types";

import PLACESLIST from "../static/places.json";

const Home = () => {
  const [libraries] = useState(["places"]);
  const [places, setPlaces] = useState([]);
  const [cards, setCards] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [modalVisbility, setModalVisibility] = useState(false);
  const [currentUserCoords, setCurrentUserCoords] = useState();
  const [hoveredCard, setHoveredCard] = useState(null);
  const [listViewActive, setListViewActive] = useState(true);
  const [isLoadingCards, setIsLoadingCards] = useState(false);
  const [cuisines, setCuisines] = useState([]);
  const [filters, setFilters] = useState({});
  const [sortOrder, setSortOrder] = useState(SORTTYPES.NEAREST);
  const [isSortActive, setIsSortActive] = useState(false);
  const [isMapView, setIsMapView] = useState(true);
  const isMobile = useMediaQuery({ query: `(max-width: 560px)` });
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: CONSTANTS.APIKEY,
    libraries,
  });
  const cardRefs = useRef([]);

  useEffect(() => {
    if (isLoaded) {
      setCurrentUserCoords({
        latitude: 32.925709,
        longitude: -96.897675,
        term: "Farmers Branch City Center",
      });
    }

    return () => {
      setPlaces([]);
      setCards([]);
      cardRefs.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  useEffect(() => {
    let list = [];
    PLACESLIST.forEach((place) => {
      if (!list.includes(place.cuisine)) list.push(place.cuisine);
    });

    setCuisines(list.sort());
  }, []);

  useEffect(() => {
    if (currentUserCoords) {
      handleFiltersAndSorting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortOrder, currentUserCoords]);

  const getGeolocation = () => {
    setIsLoadingCards(true);
    if (!navigator.geolocation) handleIPGeoLookup();

    const geoOptions = {
      enableHighAccuracy: true,
      maximumAge: Infinity,
      timeout: 6000,
    };

    const geoSuccess = async (position) => {
      if (position && position.coords) {
        position.coords.latitude === currentUserCoords?.latitude
          ? handleFiltersAndSorting()
          : setCurrentUserCoords(position.coords);
      }
    };

    const geoError = (error) => {
      console.warn(error);
      handleIPGeoLookup();
    };

    navigator.geolocation.getCurrentPosition(geoSuccess, geoError, geoOptions);
  };

  const handleIPGeoLookup = () => {
    fetch(`https://api.ipregistry.co/?key=${CONSTANTS.IPKEY}`)
      .then((response) => response.json())
      .then(async (data) => {
        if (data && data.location) {
          setCurrentUserCoords({
            latitude: data.location.latitude,
            longitude: data.location.longitude,
          });
        }
      })
      .catch((error) => {
        console.warn(error);
      });
  };

  const handleMarkerClick = (place) => {
    if (!listViewActive) {
      setSelectedPlace(place);
      setModalVisibility(true);
    } else {
      const item = cardRefs.current.find((x) => x.id === place.place_id);
      item.ref.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setSelectedPlace(place);
      setModalVisibility(true);
    }
  };

  const handleFiltersAndSorting = () => {
    let list = [...PLACESLIST];
    let items = [];

    if (sortOrder === SORTTYPES.NEAREST) {
      while (list.length) {
        const place = list.shift();
        const lat = place.geometry.location.lat;
        const lng = place.geometry.location.lng;
        const distance = calcDistance(
          lat,
          lng,
          currentUserCoords.latitude,
          currentUserCoords.longitude
        );

        items.push({
          ...place,
          coordinates: {
            latitude: lat,
            longitude: lng,
          },
          category: "restaurant",
          distance,
        });
      }
    } else if (sortOrder === SORTTYPES.ALPHABETICAL) {
      items = list
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((c) => ({
          ...c,
          coordinates: {
            latitude: c.geometry.location.lat,
            longitude: c.geometry.location.lng,
          },
        }));
    } else {
      items = list
        .sort((a, b) => b.rating - a.rating)
        .map((c) => ({
          ...c,
          coordinates: {
            latitude: c.geometry.location.lat,
            longitude: c.geometry.location.lng,
          },
        }));
    }

    if (filters.filters && filters.filters.length > 0) {
      items = items.filter((item) => filters.filters.includes(item.cuisine));
    }

    if (filters.options && filters.options.length > 0) {
      items = items.filter((item) =>
        item.transactions.some((r) => filters.options.includes(r))
      );
    }

    if (filters.filterByOpen) {
      const today = new Date();
      const day = today.getDay();
      const hours = today.getHours();
      const minutes = today.getMinutes();
      const time = Number(`${hours}${minutes}`);

      items = items.filter((item) => {
        if (!item.opening_hours?.periods) return false;
        const values = item.opening_hours?.periods?.filter(
          (period) => period.open.day === day
        );

        if (
          values.length > 0 &&
          values[0]?.open?.time &&
          values[0]?.close?.time
        ) {
          if (
            values.length > 1 &&
            values[1].open.time &&
            values[1].close.time
          ) {
            if (
              (time > values[0].open.time && time < values[0].close.time) ||
              (time > values[1].open.time && time < values[1].close.time)
            ) {
              return true;
            } else {
              return false;
            }
          } else {
            if (time > values[0].open.time && time < values[0].close.time) {
              return true;
            } else {
              return false;
            }
          }
        } else {
          return false;
        }
      });
    }

    setCards(items);
    setPlaces(items);
    setIsLoadingCards(false);
  };

  const renderShowingText = () => {
    if (currentUserCoords && currentUserCoords.term) {
      return (
        <StatusText>
          Showing {cards.length} results near
          <strong> {currentUserCoords.term}</strong>
        </StatusText>
      );
    } else if (currentUserCoords && currentUserCoords.zipCode) {
      return (
        <StatusText>
          Showing {cards.length} results near
          <strong> {currentUserCoords.zipCode}</strong>
        </StatusText>
      );
    } else {
      return (
        <StatusText>
          Showing {cards.length} results near <strong>me</strong>
        </StatusText>
      );
    }
  };

  const handleMarkerModal = (place) => {
    setSelectedPlace(place);
    setModalVisibility(true);
  };

  const handleModalClose = () => {
    setSelectedPlace(null);
    setModalVisibility(false);
  };

  const handleOriginSelection = (searchTerm) => {
    const geocoder = new window.google.maps.Geocoder();

    let address = searchTerm.description.split(",");
    address.pop();
    const value = address.join(", ");

    geocoder.geocode(
      {
        address: searchTerm.description,
      },
      async (result, status) => {
        if (status === window.google.maps.GeocoderStatus.OK) {
          if (result && result.length > 0) {
            setCurrentUserCoords({
              latitude: result[0].geometry.location.lat(),
              longitude: result[0].geometry.location.lng(),
              term: value,
            });
          }
        }
      }
    );
  };

  const renderSideBarContent = () => (
    <>
      <SidebarHeader>
        {renderShowingText()}
        <SortByContainer>
          <Selected
            className={isSortActive && "active"}
            onClick={() => setIsSortActive(!isSortActive)}
          >
            {sortOrder}
            <ChevronDown size={18} />
          </Selected>
          <SortList className={isSortActive && "active"}>
            <SortItem
              onClick={() => {
                setSortOrder(SORTTYPES.NEAREST);
                setIsSortActive(false);
              }}
            >
              {SORTTYPES.NEAREST}
            </SortItem>
            <SortItem
              onClick={() => {
                setSortOrder(SORTTYPES.ALPHABETICAL);
                setIsSortActive(false);
              }}
            >
              {SORTTYPES.ALPHABETICAL}
            </SortItem>
            <SortItem
              onClick={() => {
                setSortOrder(SORTTYPES.RATING);
                setIsSortActive(false);
              }}
            >
              {SORTTYPES.RATING}
            </SortItem>
          </SortList>
        </SortByContainer>
      </SidebarHeader>
      {isMapView ? (
        <>
          {cards && cards.length > 0 && (
            <PlacesListContainer>
              {cards
                .sort((a, b) => a.distance - b.distance)
                .map((place) => (
                  <CardContainer
                    key={place.place_id}
                    ref={(ref) =>
                      cardRefs.current.push({ id: place.place_id, ref })
                    }
                  >
                    <Card
                      details={place}
                      onSelection={(place) => handleMarkerModal(place)}
                      onCardMouseOver={(place) => setHoveredCard(place)}
                    />
                  </CardContainer>
                ))}
            </PlacesListContainer>
          )}
        </>
      ) : (
        <>
          <ListViewPlacesContainer>
            {cards &&
              cards.length > 0 &&
              cards
                .sort((a, b) => a.distance - b.distance)
                .map((place) => (
                  <ListViewPlace>
                    <div
                      key={`${place.place_id}-list-view`}
                      ref={(ref) =>
                        cardRefs.current.push({ id: place.place_id, ref })
                      }
                    >
                      <Card
                        details={place}
                        onSelection={(place) => handleMarkerModal(place)}
                        onCardMouseOver={(place) => setHoveredCard(place)}
                      />
                    </div>
                  </ListViewPlace>
                ))}
          </ListViewPlacesContainer>
        </>
      )}
      {!isLoadingCards && (!cards || cards.length === 0) && (
        <NoResults>
          <Title>No Results</Title>
          <p>Sorry, we couldn't find any results. Please try again later.</p>
        </NoResults>
      )}
      {isLoadingCards && (
        <NoResults>
          <Title>Please Wait...</Title>
          <p>Capturing restuarnts and recalculating distance.</p>
        </NoResults>
      )}
    </>
  );

  return (
    <Layout className={"mappie-container"}>
      <Header
        cuisines={cuisines}
        isMapView={isMapView}
        onNearMeClick={() => {
          setCards([]);
          setPlaces([]);
          getGeolocation();
        }}
        onFilterApplyClick={(values) => setFilters(values)}
        onViewChangeClick={() => setIsMapView(!isMapView)}
        onOriginSelection={handleOriginSelection}
      />
      {!isLoaded && <Preloader />}
      {isLoaded && (
        <>
          <SidebarContainer
            active={listViewActive}
            className={isMapView ? "map-view" : "list-view"}
          >
            <Flexbox>
              {isMapView ? (
                renderSideBarContent()
              ) : (
                <CardsContainer>{renderSideBarContent()}</CardsContainer>
              )}
              <SidebarFooter>
                <AccountDetailsHeading size={5}>
                  Farmers Branch Restaurant Finder
                </AccountDetailsHeading>
                <GetMappie href={"https://www.mappie.io"} target={"_blank"}>
                  Get <Logo />
                </GetMappie>
              </SidebarFooter>
            </Flexbox>
          </SidebarContainer>
          <MapContainer
            places={places}
            isReady={true}
            onMarkerClick={handleMarkerClick}
            userCoords={currentUserCoords}
            hoveredCard={hoveredCard}
          />
          <Legend />
        </>
      )}
      {modalVisbility && (
        <Modal onModalClose={handleModalClose}>
          <DetailsPopup place={selectedPlace} />
        </Modal>
      )}
      {isLoaded && isMobile && (
        <>
          <FooterButtonContainer>
            <FooterButton
              bgColor={listViewActive ? palette.brightYellow : palette.primary}
              color={listViewActive ? palette.primary : palette.brightYellow}
              onClick={() => setListViewActive(false)}
            >
              <Map style={{ marginRight: 10 }} /> Map View
            </FooterButton>
            <FooterButton
              bgColor={listViewActive ? palette.primary : palette.brightYellow}
              color={listViewActive ? palette.brightYellow : palette.primary}
              onClick={() => setListViewActive(true)}
            >
              <List style={{ marginRight: 10 }} /> List View
            </FooterButton>
          </FooterButtonContainer>
        </>
      )}
    </Layout>
  );
};

export default Home;

const Layout = styled.main`
  max-width: 100%;
  padding: 0;
`;

const SidebarContainer = styled.div`
  border-radius: 30px;
  background-color: ${palette.white};
  position: absolute;
  overflow: hidden;
  max-width: 100%;
  z-index: 3;
  border: 1px solid ${palette.dark};
  box-shadow: 0px 3px 6px ${palette.shadowColor};
  transform: ${({ active }) =>
    active ? `translateY(0)` : `translateY(-100vh)`};
  transition: transform 300ms ease-in;
  top: 70px;
  left: 0;
  bottom: 56px;

  ${mediaMin.tablet_md`
    top: 0px;
    left: calc(50% - 320px);
    width: 50%;
    display: block;
    top: 100px;
    left: 20px;
    bottom: 30px;
  `}

  ${mediaMin.laptop`
    width: 55%;
    top: 100px;
    left: 30px;
    bottom: 30px;
  `}

  ${mediaMin.desktop`
    top: 100px;
    left: 30px;
    bottom: 30px;
    width: 40%;
  `}

  &.list-view {
    ${mediaMin.tablet_md`
      top: 70px;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      border-radius: 0;
      box-shadow: none;
      border: none;
    `}
  }
`;

const CardsContainer = styled.div`
  height: calc(100% - 60px);
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
`;

const CardContainer = styled.div`
  margin-bottom: 20px;
`;

const PlacesListContainer = styled.div`
  height: 100%;
  overflow-y: scroll;
  padding: 10px 10px 0;
`;

const ListViewPlacesContainer = styled.div`
  overflow-y: scroll;
  padding: 10px;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 20px;
`;

const ListViewPlace = styled.div`
  width: calc(50% - 20px);
`;

const SidebarFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 10px;
  margin: 0 10px;
  border-top: 1px solid ${palette.dark};
  position: relative;
  background-color: ${palette.white};

  &::before {
    content: "";
    position: absolute;
    top: -21px;
    left: 0;
    right: 0;
    height: 20px;
    background: rgb(255, 255, 255);
    background: linear-gradient(
      0deg,
      rgba(255, 255, 255, 1) 0%,
      rgba(255, 255, 255, 0) 100%
    );
  }
`;

const AccountDetailsHeading = styled.h2`
  font-size: 14px;
  line-height: 22px;
  color: ${palette.primary};
  font-family: ${fonts.primary};

  ${mediaMin.laptop`
    font-size: 21px;
    line-height: 28px;
  `};
`;

const Flexbox = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const GetMappie = styled.a`
  display: flex;
  align-items: center;
  font-size: 13px;
  color: ${palette.dark};
  text-decoration: none;

  &:hover {
    text-decoration: none;
    color: ${palette.dark};
  }

  svg {
    height: 100%;
    width: 100px;
    margin-left: 10px;

    ${mediaMin.tablet_md`
      width: 120px;
    `}
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 20px 0 10px;
`;

const StatusText = styled.p`
  padding: 0 20px;
  font-size: 11px;
  line-height: 1.25;

  strong {
    font-weight: 600;
  }

  ${mediaMin.tablet_md`
    font-size: 13px;
  `}
`;

const FooterButtonContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 999999;
  width: 100%;
  display: flex;
  align-items: center;
`;

const FooterButton = styled.button`
  width: 100%;
  border: none;
  background-color: ${({ bgColor }) => bgColor};
  color: ${({ color }) => color};
  padding: 15px 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NoResults = styled.div`
  height: 100%;
  overflow-y: scroll;
  padding: 10px 10px 0;
  text-align: center;

  p {
    font-size: 12px;
  }
`;

const Title = styled.h3`
  font-family: ${fonts.primary};
  margin: 20px 0 10px;
  color: ${palette.primary};
`;

const SortByContainer = styled.div`
  position: relative;
  margin-right: 30px;
  font-size: 13px;
  z-index: 99;
`;

const Selected = styled.div`
  border-radius: 20px;
  border: 1px solid ${palette.dark};
  display: flex;
  align-items: center;
  padding: 4px 10px 4px 15px;
  background-color: ${palette.white};

  & > svg {
    transition: transform 300ms ease-in-out;
  }

  &.active > svg {
    transform: rotate(180deg);
  }

  &:hover {
    cursor: pointer;
  }
`;

const SortList = styled.ul`
  border-radius: 0 0 10px 10px;
  border: 1px solid ${palette.dark};
  position: absolute;
  top: 15px;
  left: 0;
  right: 0;
  z-index: -1;
  padding: 15px 0 5px;
  background-color: ${palette.tan};
  display: none;

  &.active {
    display: block;
  }
`;

const SortItem = styled.li`
  padding: 5px;

  &:hover {
    cursor: pointer;
    color: ${palette.primary};
  }
`;
